import { formatEther, parseEther } from "viem";
import { useReadContract } from "wagmi";
import { GATEWAY_ABI } from "@/contract/abi/gateway";
import ContractAddress from "@/contract/addresses";
import { useEffectiveAddress } from "@/hooks/auth/useEffectiveAddress";
import { useLegacyUserDnmWithdrawn } from "./useLegacyDnmWithdrawn";

export type UseFetchContractUserReturn = ReturnType<
  typeof useFetchContractUser
>;

const HUNDRED_BV_WEI = parseEther("100");
const REWARD_PER_HUNDRED_BV_WEI = parseEther("0.08");

export const useFetchContractUser = () => {
  const address = useEffectiveAddress();

  const user = useReadContract({
    address: ContractAddress.GATEWAY,
    abi: GATEWAY_ABI,
    functionName: "getUser",
    args: [address as `0x${string}`],
    query: {
      enabled: !!address,
    },
  });

  const legacy = useLegacyUserDnmWithdrawn(
    address as `0x${string}` | undefined,
  );

  // Mirror Solidity DnmFaucetLib.withdrawPersonal exactly:
  //
  //   earned    = (bv.gross_summ / 100e18) * 0.08e18
  //   withdrawn = oldGateway.getUser(me).bv.dnm_withdraw + bv.dnm_withdraw
  //   claimable = earned > withdrawn ? earned - withdrawn : 0
  //
  // Two things differ from V1 and both inflate the figure if missed:
  //   - GROSS, not `summ`. V2's `summ` is net of the referral cut, so entitle-
  //     ment computed from it comes out ~10% short.
  //   - Withdrawals are counted across BOTH gateways. Migration imports users
  //     with V2's `dnm_withdraw` at zero (that is what stops migrated users
  //     being paid twice), so subtracting only V2's counter hands every
  //     migrated user their entire history back as "available" and the
  //     withdraw reverts with DnmEntitlementExceeded.
  //
  // Integer division floors to whole 100-BV increments; Big.js exact decimal
  // division produced phantom dust that left the withdraw button enabled after
  // users had drained on-chain.
  // `gross_summ` exists only on V2. On V1 there was no referral cut, so the
  // gross figure IS `summ` — falling back to it keeps V1 builds correct rather
  // than showing every user a flat zero.
  const grossSumm = user.data?.bv.gross_summ ?? user.data?.bv.summ ?? 0n;
  const dnmWithdrawn = (user.data?.bv.dnm_withdraw ?? 0n) + legacy.withdrawn;
  const earned = (grossSumm / HUNDRED_BV_WEI) * REWARD_PER_HUNDRED_BV_WEI;
  const final_value = earned > dnmWithdrawn ? earned - dnmWithdrawn : 0n;
  // Withholding the number until the legacy read lands avoids flashing an
  // overstated balance that the contract would refuse to pay.
  const dnmResolved = !!user.data && !legacy.isLoading;

  return {
    id: user.data?.id,
    dnm_available_amount: dnmResolved ? formatEther(final_value) : 0,
    pending_voucher: +formatEther(user.data?.pending_voucher || BigInt(0)),
    pending_withdraw: +formatEther(user.data?.pending_withdraw || BigInt(0)),
    // Net of the referral cut -- this is the user-facing "your BV" figure
    // (level progress, etc). Only the DNM purchase reward uses GROSS, via
    // bv_sum_gross below, to match the on-chain entitlement formula.
    bv_sum: +formatEther(user.data?.bv.summ ?? BigInt(0)),
    bv_sum_gross: +formatEther(grossSumm),
    level: user.data?.bv.level,
    bv: user.data?.bv,
    isLoading: user.isLoading,
    last_order: user.data?.last_order.date,
    last_order_id: user.data?.last_order.order_id,
    refetch: user.refetch,
  };
};
